import { AddProfileDialog } from '@/components/forward/add-profile-dialog'
import { AddRecipientDialog } from '@/components/forward/add-recipient-dialog'
import { SettingsDialog } from '@/components/settings/settings-dialog'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from '@/components/ui/sidebar'
import { logger } from '@/lib/logger'
import { useAuthStore } from '@/stores/auth-store'
import { useCustomerStore } from '@/stores/customer-store'
import { useDevicesStore } from '@/stores/devices-store'
import { BarChart3, ChevronDown, ChevronRight, ChevronUp, Forward, Globe, LogOut, MessageSquare, Phone, Plus, Settings as SettingsIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

// Standard navigation for regular devices (titleKey references translation keys)
const standardNavigationItems = [
    {
        titleKey: 'navigation.activity',
        id: 'activity',
        url: '/customer/activity',
        icon: BarChart3,
    },
    {
        titleKey: 'navigation.calls',
        id: 'calls',
        url: '/customer/calls',
        icon: Phone,
    },
    {
        titleKey: 'navigation.answers',
        id: 'knowledge',
        url: '/customer/knowledge',
        icon: MessageSquare,
    },
    {
        titleKey: 'navigation.forward',
        id: 'forward',
        url: '/customer/forward',
        icon: Forward,
    },
]

// MDS-specific navigation (3 separate pages)
const mdsNavigationItems = [
    {
        titleKey: 'navigation.activity',
        id: 'mds-activity',
        url: '/customer/mds-activity',
        icon: BarChart3,
    },
    {
        titleKey: 'navigation.calls',
        id: 'mds-calls',
        url: '/customer/mds-calls',
        icon: Phone,
    },
    {
        titleKey: 'navigation.configuration',
        id: 'mds-configuration',
        url: '/customer/mds-configuration',
        icon: SettingsIcon,
    },
]

export function AppSidebar() {
    const { t, i18n } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const [isKnowledgeExpanded, setIsKnowledgeExpanded] = useState(false)
    const [isForwardExpanded, setIsForwardExpanded] = useState(false)
    const [isSettingsOpen, setIsSettingsOpen] = useState(false)
    const [showAddProfileDialog, setShowAddProfileDialog] = useState(false)
    const [showAddRecipientDialog, setShowAddRecipientDialog] = useState(false)
    const { answerCategories, loadCategories, setCurrentCategory, getAnswerCountForCategory, callhandlingProfiles, forwardRecipients, loadCallHandlingProfiles, loadForwardRecipients, selectForwardProfile, selectForwardRecipient, selectedForwardProfileId, selectedForwardRecipientId } = useCustomerStore()
    const { user, userLogout } = useAuthStore()
    const { currentDevice, isMdsDevice } = useDevicesStore()

    // Determine which navigation items to show based on device type
    const navigationItems = useMemo(() => {
        const isMds = currentDevice?.type === 'DEVICE_SELECTOR'
        logger.debug('Navigation items update', { isMds, deviceType: currentDevice?.type })
        return isMds ? mdsNavigationItems : standardNavigationItems
    }, [currentDevice])

    // Load categories and forward data when component mounts (only for non-MDS devices)
    useEffect(() => {
        const isMds = currentDevice?.type === 'DEVICE_SELECTOR'
        if (!isMds) {
            loadCategories()
            loadCallHandlingProfiles()
            loadForwardRecipients()
        }
    }, [loadCategories, loadCallHandlingProfiles, loadForwardRecipients, currentDevice])

    // Check if current path is knowledge or forward related
    const isKnowledgeActive = location.pathname.startsWith('/customer/knowledge')
    const isForwardActive = location.pathname.startsWith('/customer/forward')

    // Auto-expand if we're on a knowledge page
    useEffect(() => {
        if (isKnowledgeActive) {
            setIsKnowledgeExpanded(true)
        }
    }, [isKnowledgeActive])

    // Auto-expand if we're on a forward page
    useEffect(() => {
        if (isForwardActive) {
            setIsForwardExpanded(true)
        }
    }, [isForwardActive])

    // Auto-redirect to MDS activity when MDS device is selected
    useEffect(() => {
        const isMds = currentDevice?.type === 'DEVICE_SELECTOR'
        const isOnMdsPage = location.pathname.startsWith('/customer/mds-')
        const isOnStandardPage = location.pathname.startsWith('/customer/') && !isOnMdsPage

        logger.debug('Auto-redirect check', {
            isMds,
            deviceType: currentDevice?.type,
            isOnMdsPage,
            isOnStandardPage,
            currentPath: location.pathname
        })

        if (isMds && isOnStandardPage) {
            // MDS device selected but on standard page -> redirect to MDS activity
            logger.debug('Redirecting to MDS activity')
            navigate('/customer/mds-activity', { replace: true })
        } else if (!isMds && isOnMdsPage) {
            // Standard device selected but on MDS page -> redirect to activity
            logger.debug('Redirecting to activity page')
            navigate('/customer/activity', { replace: true })
        }
    }, [currentDevice, location.pathname, navigate])

    const handleLogout = () => {
        userLogout()
    }

    const getUserInitials = () => {
        if (user?.firstname && user?.lastname) {
            return `${user.firstname[0]}${user.lastname[0]}`.toUpperCase()
        }
        if (user?.email) {
            return user.email.substring(0, 2).toUpperCase()
        }
        return 'U'
    }

    return (
        <>
            <Sidebar variant="inset">
                <SidebarContent>
                    <SidebarGroup>
                        <SidebarGroupLabel className="text-base font-semibold mb-4">
                            Assistant AI
                        </SidebarGroupLabel>
                        <SidebarGroupContent>
                            <SidebarMenu className="space-y-1">
                                {navigationItems.map((item) => {
                                    // Special handling for Wissen item (only for standard navigation)
                                    if (item.id === 'knowledge' && !isMdsDevice()) {
                                        return (
                                            <SidebarMenuItem key={item.id}>
                                                <SidebarMenuButton
                                                    asChild
                                                    isActive={isKnowledgeActive}
                                                    className="w-full justify-between"
                                                >
                                                    <Link
                                                        to={item.url}
                                                        className="flex items-center justify-between w-full px-3 py-2"
                                                        onClick={(e) => {
                                                            e.preventDefault()
                                                            // Navigate to the main knowledge page
                                                            navigate(item.url)
                                                            // Toggle dropdown
                                                            setIsKnowledgeExpanded(!isKnowledgeExpanded)
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <item.icon className="h-4 w-4" />
                                                            <span>{t(item.titleKey)}</span>
                                                        </div>
                                                        {isKnowledgeExpanded ? (
                                                            <ChevronDown className="h-4 w-4" />
                                                        ) : (
                                                            <ChevronRight className="h-4 w-4" />
                                                        )}
                                                    </Link>
                                                </SidebarMenuButton>
                                                {isKnowledgeExpanded && (
                                                    <SidebarMenuSub>
                                                        {/* Favorites category first */}
                                                        <SidebarMenuSubItem>
                                                            <SidebarMenuSubButton
                                                                asChild
                                                                isActive={location.pathname === `/customer/knowledge/favorites`}
                                                            >
                                                                <Link
                                                                    to={`/customer/knowledge/favorites`}
                                                                    className="!text-gray-900 !font-medium hover:!text-brand-primary hover:!bg-brand-primary/10 data-[active=true]:!text-brand-primary data-[active=true]:!bg-brand-primary/10"
                                                                >
                                                                    <span className="text-sm !text-gray-900">{t('sidebar.favorites')}</span>
                                                                </Link>
                                                            </SidebarMenuSubButton>
                                                        </SidebarMenuSubItem>

                                                        {answerCategories.length === 0 ? (
                                                            <SidebarMenuSubItem>
                                                                <div className="w-full pl-8 py-2 text-sm text-muted-foreground">
                                                                    {t('sidebar.noCategories')}
                                                                </div>
                                                            </SidebarMenuSubItem>
                                                        ) : (
                                                            answerCategories
                                                                .filter(category => getAnswerCountForCategory(category) > 0)
                                                                .map((category) => (
                                                                    <SidebarMenuSubItem key={category.id}>
                                                                        <SidebarMenuSubButton
                                                                            asChild
                                                                            isActive={location.pathname === `/customer/knowledge/${category.id}`}
                                                                        >
                                                                            <Link
                                                                                to={`/customer/knowledge/${category.id}`}
                                                                                className="!text-gray-900 !font-medium hover:!text-brand-primary hover:!bg-brand-primary/10 data-[active=true]:!text-brand-primary data-[active=true]:!bg-brand-primary/10"
                                                                                onClick={() => setCurrentCategory(category)}
                                                                            >
                                                                                <span className="text-sm !text-gray-900">{category.title || category.name}</span>
                                                                            </Link>
                                                                        </SidebarMenuSubButton>
                                                                    </SidebarMenuSubItem>
                                                                ))
                                                        )}
                                                    </SidebarMenuSub>
                                                )}
                                            </SidebarMenuItem>
                                        )
                                    }

                                    // Special handling for Weiterleitung item (only for standard navigation)
                                    if (item.id === 'forward' && !isMdsDevice()) {
                                        return (
                                            <SidebarMenuItem key={item.id}>
                                                <SidebarMenuButton
                                                    asChild
                                                    isActive={isForwardActive}
                                                    className="w-full justify-between"
                                                >
                                                    <Link
                                                        to={item.url}
                                                        className="flex items-center justify-between w-full px-3 py-2"
                                                        onClick={(e) => {
                                                            e.preventDefault()
                                                            // Toggle dropdown
                                                            setIsForwardExpanded(!isForwardExpanded)
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <item.icon className="h-4 w-4" />
                                                            <span>{t(item.titleKey)}</span>
                                                        </div>
                                                        {isForwardExpanded ? (
                                                            <ChevronDown className="h-4 w-4" />
                                                        ) : (
                                                            <ChevronRight className="h-4 w-4" />
                                                        )}
                                                    </Link>
                                                </SidebarMenuButton>
                                                {isForwardExpanded && (
                                                    <SidebarMenuSub>
                                                        {/* Profiles Section */}
                                                        <SidebarMenuSubItem>
                                                            <div className="w-full pl-2 py-1.5 flex items-center justify-between">
                                                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                                    {t('sidebar.profiles')}
                                                                </span>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        setShowAddProfileDialog(true)
                                                                    }}
                                                                    className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                                                    title={t('sidebar.newProfile')}
                                                                >
                                                                    <Plus className="h-3.5 w-3.5" />
                                                                </button>
                                                            </div>
                                                        </SidebarMenuSubItem>
                                                        {callhandlingProfiles.length === 0 ? (
                                                            <SidebarMenuSubItem>
                                                                <div className="w-full pl-6 py-1.5 text-sm text-muted-foreground">
                                                                    {t('sidebar.noProfiles')}
                                                                </div>
                                                            </SidebarMenuSubItem>
                                                        ) : (
                                                            callhandlingProfiles.map((profile) => (
                                                                <SidebarMenuSubItem key={profile.id}>
                                                                    <SidebarMenuSubButton
                                                                        isActive={selectedForwardProfileId === profile.id}
                                                                        onClick={() => {
                                                                            selectForwardProfile(profile.id)
                                                                            navigate('/customer/forward')
                                                                        }}
                                                                        className="cursor-pointer !text-gray-900 !font-medium hover:!text-brand-primary hover:!bg-brand-primary/10 data-[active=true]:!text-brand-primary data-[active=true]:!bg-brand-primary/10 pl-4"
                                                                    >
                                                                        <span className="text-sm !text-gray-900">{profile.title || profile.name}</span>
                                                                    </SidebarMenuSubButton>
                                                                </SidebarMenuSubItem>
                                                            ))
                                                        )}

                                                        {/* Recipients Section */}
                                                        <SidebarMenuSubItem>
                                                            <div className="w-full pl-2 py-1.5 mt-2 flex items-center justify-between">
                                                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                                    {t('sidebar.recipients')}
                                                                </span>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        setShowAddRecipientDialog(true)
                                                                    }}
                                                                    className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                                                    title={t('sidebar.newRecipient')}
                                                                >
                                                                    <Plus className="h-3.5 w-3.5" />
                                                                </button>
                                                            </div>
                                                        </SidebarMenuSubItem>
                                                        {forwardRecipients.length === 0 ? (
                                                            <SidebarMenuSubItem>
                                                                <div className="w-full pl-6 py-1.5 text-sm text-muted-foreground">
                                                                    {t('sidebar.noRecipients')}
                                                                </div>
                                                            </SidebarMenuSubItem>
                                                        ) : (
                                                            forwardRecipients.map((recipient) => (
                                                                <SidebarMenuSubItem key={recipient.id}>
                                                                    <SidebarMenuSubButton
                                                                        isActive={selectedForwardRecipientId === recipient.id}
                                                                        onClick={() => {
                                                                            selectForwardRecipient(recipient.id)
                                                                            navigate('/customer/forward')
                                                                        }}
                                                                        className="cursor-pointer !text-gray-900 !font-medium hover:!text-brand-primary hover:!bg-brand-primary/10 data-[active=true]:!text-brand-primary data-[active=true]:!bg-brand-primary/10 pl-4"
                                                                    >
                                                                        <span className="text-sm !text-gray-900">{recipient.title}</span>
                                                                    </SidebarMenuSubButton>
                                                                </SidebarMenuSubItem>
                                                            ))
                                                        )}
                                                    </SidebarMenuSub>
                                                )}
                                            </SidebarMenuItem>
                                        )
                                    }

                                    // Regular navigation items
                                    return (
                                        <SidebarMenuItem key={item.id}>
                                            <SidebarMenuButton
                                                asChild
                                                isActive={location.pathname === item.url}
                                                className="w-full justify-start"
                                            >
                                                <Link to={item.url} className="flex items-center gap-3 px-3 py-2">
                                                    <item.icon className="h-4 w-4" />
                                                    <span>{t(item.titleKey)}</span>
                                                </Link>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    )
                                })}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                </SidebarContent>

                <SidebarFooter>
                    <SidebarMenu>
                        <SidebarMenuItem>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <SidebarMenuButton
                                        size="lg"
                                        className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                                    >
                                        <Avatar className="h-8 w-8 rounded-lg">
                                            <AvatarFallback className="rounded-lg bg-brand-primary text-white">
                                                {getUserInitials()}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="grid flex-1 text-left text-sm leading-tight">
                                            <span className="truncate font-semibold">
                                                {user?.name || user?.email || t('sidebar.user')}
                                            </span>
                                            <span className="truncate text-xs text-muted-foreground">
                                                {user?.email || ''}
                                            </span>
                                        </div>
                                        <ChevronUp className="ml-auto size-4" />
                                    </SidebarMenuButton>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                                    side="bottom"
                                    align="end"
                                    sideOffset={4}
                                >
                                    <DropdownMenuLabel className="p-0 font-normal">
                                        <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                            <Avatar className="h-8 w-8 rounded-lg">
                                                <AvatarFallback className="rounded-lg bg-brand-primary text-white">
                                                    {getUserInitials()}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="grid flex-1 text-left text-sm leading-tight">
                                                <span className="truncate font-semibold">
                                                    {user?.name || user?.email || t('sidebar.user')}
                                                </span>
                                                <span className="truncate text-xs text-muted-foreground">
                                                    {user?.email || ''}
                                                </span>
                                            </div>
                                        </div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => setIsSettingsOpen(true)}>
                                        <SettingsIcon className="mr-2 h-4 w-4" />
                                        {t('navigation.settings')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={() => i18n.changeLanguage(i18n.language === 'de' ? 'en' : 'de')}
                                    >
                                        <Globe className="mr-2 h-4 w-4" />
                                        {i18n.language === 'de' ? 'English' : 'Deutsch'}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout}>
                                        <LogOut className="mr-2 h-4 w-4" />
                                        {t('global.button.logout')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarFooter>
            </Sidebar>
            <SettingsDialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen} />

            {/* Forward Dialogs */}
            <AddProfileDialog
                open={showAddProfileDialog}
                onOpenChange={setShowAddProfileDialog}
                onSuccess={() => {
                    loadCallHandlingProfiles()
                    setShowAddProfileDialog(false)
                }}
                recipients={forwardRecipients}
            />
            <AddRecipientDialog
                open={showAddRecipientDialog}
                onOpenChange={setShowAddRecipientDialog}
                onSuccess={() => {
                    loadForwardRecipients()
                    setShowAddRecipientDialog(false)
                }}
            />
        </>
    )
}